import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Stack, MenuItem, Box, Typography, alpha, Alert,
} from '@mui/material';
import type { HubService } from '../../constants/serviceHubs';
import { resolveServiceIcon } from '../../constants/serviceIcons';

const ICON_OPTIONS = [
  'Restaurant', 'ShoppingCart', 'DeliveryDining', 'LocalMall', 'Storefront',
  'Fastfood', 'LocalCafe', 'Pets', 'Medication', 'LocalShipping',
];

const COLOR_PRESETS = ['#ea580c', '#16a34a', '#2563eb', '#db2777', '#7c3aed', '#0891b2', '#ca8a04'];

export type HubServiceFormValues = {
  name: string;
  description: string;
  icon: string;
  themeColor: string;
  platformModule: 'food' | 'grocery';
};

type Props = {
  open: boolean;
  mode: 'create' | 'edit';
  initial?: Partial<HubServiceFormValues> | null;
  saving?: boolean;
  error?: string | null;
  allowedModules?: Array<'food' | 'grocery'>;
  onClose: () => void;
  onSubmit: (values: HubServiceFormValues) => void;
};

const EMPTY: HubServiceFormValues = {
  name: '',
  description: '',
  icon: 'Restaurant',
  themeColor: '#ea580c',
  platformModule: 'food',
};

export function valuesFromService(service: HubService): HubServiceFormValues {
  const settings = (service.settings || {}) as Record<string, unknown>;
  const module =
    (settings.platformModule as string)
    || (service as HubService & { moduleKey?: string }).moduleKey
    || service.serviceCode
    || 'food';
  return {
    name: service.name || '',
    description: service.description || '',
    icon: service.icon || (module === 'grocery' ? 'ShoppingCart' : 'Restaurant'),
    themeColor: service.themeColor || (module === 'grocery' ? '#16a34a' : '#ea580c'),
    platformModule: module === 'grocery' ? 'grocery' : 'food',
  };
}

export default function HubServiceFormDialog({
  open, mode, initial, saving, error, allowedModules = ['food', 'grocery'], onClose, onSubmit,
}: Props) {
  const [form, setForm] = useState<HubServiceFormValues>(EMPTY);
  const moduleOptions = allowedModules.length ? allowedModules : ['food', 'grocery'];

  useEffect(() => {
    if (!open) return;
    const next = { ...EMPTY, ...initial };
    if (!moduleOptions.includes(next.platformModule)) {
      next.platformModule = moduleOptions[0];
      next.icon = next.platformModule === 'grocery' ? 'ShoppingCart' : 'Restaurant';
      next.themeColor = next.platformModule === 'grocery' ? '#16a34a' : '#ea580c';
    }
    setForm(next);
  }, [open, initial, moduleOptions.join('|')]);

  const canSave = form.name.trim().length >= 2 && !!form.platformModule;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
        {mode === 'create' ? 'Add delivery sub-service' : 'Edit sub-service'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            autoFocus
            label="Name"
            fullWidth
            size="small"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Pharmacy Delivery"
          />

          <TextField
            label="Description"
            fullWidth
            size="small"
            multiline
            minRows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />

          {mode === 'create' && (
            <TextField
              select
              label="Service type"
              fullWidth
              size="small"
              value={form.platformModule}
              onChange={(e) => {
                const platformModule = e.target.value as 'food' | 'grocery';
                setForm({
                  ...form,
                  platformModule,
                  icon: platformModule === 'grocery' ? 'ShoppingCart' : 'Restaurant',
                  themeColor: platformModule === 'grocery' ? '#16a34a' : '#ea580c',
                });
              }}
              helperText="Custom services use Food or Grocery order flows"
            >
              {moduleOptions.includes('food') && (
                <MenuItem value="food">Food-style (restaurants / meals)</MenuItem>
              )}
              {moduleOptions.includes('grocery') && (
                <MenuItem value="grocery">Grocery-style (stores / catalog)</MenuItem>
              )}
            </TextField>
          )}

          <TextField
            select
            label="Icon"
            fullWidth
            size="small"
            value={form.icon}
            onChange={(e) => setForm({ ...form, icon: e.target.value })}
          >
            {ICON_OPTIONS.map((icon) => (
              <MenuItem key={icon} value={icon}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ display: 'inline-flex', color: form.themeColor }}>{resolveServiceIcon(icon)}</Box>
                  <span>{icon}</span>
                </Stack>
              </MenuItem>
            ))}
          </TextField>

          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ mb: 0.75, display: 'block' }}>
              Theme color
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              {COLOR_PRESETS.map((c) => (
                <Box
                  key={c}
                  onClick={() => setForm({ ...form, themeColor: c })}
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1.25,
                    bgcolor: c,
                    cursor: 'pointer',
                    border: form.themeColor === c ? `2px solid ${c}` : '2px solid transparent',
                    outline: form.themeColor === c ? `2px solid ${alpha(c, 0.35)}` : 'none',
                    outlineOffset: 1,
                  }}
                />
              ))}
              <TextField
                type="color"
                size="small"
                value={form.themeColor}
                onChange={(e) => setForm({ ...form, themeColor: e.target.value })}
                sx={{ width: 56, '& input': { p: 0.5, height: 28 } }}
              />
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={!!saving} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canSave || saving}
          onClick={() => onSubmit(form)}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          {saving ? 'Saving…' : mode === 'create' ? 'Add service' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
